import {
  AsYouType,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";

// `digits` is the wire format this component has always used: calling code
// + national number, concatenated, no "+", no separators (what
// react-phone-input-2's onChange used to hand back). Kept as-is so existing
// call sites — which do `\`+${digits.replace(/^\+/, "")}\`` before sending
// to the API — don't need to change.
export function toE164(digits) {
  return digits ? `+${digits.replace(/^\+/, "")}` : "";
}

// Digit-group sizes for a country, read off the shape of its own example
// number: UA "+380 50 123 4567" → [2, 3, 4]. Used as the fallback mask
// below. Cached per country — the metadata is static for the session.
const groupSizesByCountry = new Map();

function groupSizes(country) {
  if (!groupSizesByCountry.has(country)) {
    const example = getExampleNumber(country, examples);
    const national = example
      ? example.formatInternational().replace(/^\+\d+\s*/, "")
      : "";
    const sizes = national
      .split(/\D+/)
      .filter(Boolean)
      .map((group) => group.length);
    groupSizesByCountry.set(country, sizes.length > 1 ? sizes : null);
  }
  return groupSizesByCountry.get(country);
}

function groupDigits(digits, sizes) {
  const parts = [];
  let at = 0;
  for (const size of sizes) {
    if (at >= digits.length) break;
    parts.push(digits.slice(at, at + size));
    at += size;
  }
  // Anything past the last group (only reachable for a number longer than
  // the country's own shape) trails along rather than being dropped.
  if (at < digits.length) parts.push(digits.slice(at));
  return parts.join(" ");
}

// Formats `nationalDigits` (the E.164 significant number — no trunk prefix,
// no calling code) for display in the national-number field, as one stable
// mask for the selected country.
//
// Deliberately NOT libphonenumber-js's own as-you-type formatting, which
// groups by whichever national format pattern the digits currently match.
// That sounds better and behaves worse: the matched pattern changes as the
// number grows, so the grouping visibly reshuffles while you type (UA
// "534" → "53 4", then "53453" → "5345 3", then ten digits → ungrouped
// entirely, because no UA pattern accepts a 53 prefix at all). A mask that
// keeps changing shape is worse than no mask. One grouping per country,
// applied to whatever digits are there — valid prefix or not — is what
// actually reads as a mask.
//
// The cost is losing per-country punctuation for valid numbers (US shows
// "201 555 0123" rather than libphonenumber-js's "(201) 555-0123"), and
// that a number longer than the country's own shape trails its extra
// digits in a last group. Both are worth a mask that holds still.
export function formatNational(nationalDigits, country) {
  if (!nationalDigits) return "";

  const sizes = groupSizes(country);
  if (sizes) return groupDigits(nationalDigits, sizes);

  // No example number for this country (rare — a few territories): fall
  // back to libphonenumber-js via the international form, since the
  // national-format templates expect a trunk prefix that isn't part of
  // `nationalDigits`, and would leave it ungrouped.
  const callingCode = getCountryCallingCode(country);
  const international = new AsYouType().input(`+${callingCode}${nationalDigits}`);
  return international.replace(new RegExp(`^\\+${callingCode}\\s*`), "");
}

// E.164 allows 15 digits at most, calling code included. It's the only
// upper bound for countries whose metadata declares no maximum national
// length (Germany, for one — libphonenumber-js keeps answering "OK" well
// past 13 national digits), so it backs up the per-country trim below.
const E164_MAX_DIGITS = 15;

// The only characters a phone number field may hold: digits, and a single
// leading "+" marking the input as international rather than national.
// Everything else a user can type or paste — letters, punctuation, the
// repeated "+" of "+++dfgfdgdfgh" — is dropped on the way in, so the field
// can never end up holding something that isn't a phone number.
//
// Separators the user types themselves (spaces, dashes, parentheses) are
// dropped too: the display is re-derived from the digits by formatNational,
// so keeping them would only fight the mask.
//
// Returns the two things the caller needs to interpret the input: whether it
// was meant as an international number, and its digits (pre-trimmed to the
// E.164 maximum, so an endless paste can't be handed on to a parser).
export function sanitizeInput(raw) {
  const text = String(raw ?? "");
  // A "+" counts only where it opens the number: "050+" is someone typing
  // past a national number, not the start of an international one.
  const international = /^\s*\+/.test(text);
  const digits = text.replace(/\D/g, "").slice(0, E164_MAX_DIGITS);
  return { international, digits };
}

// Caps typed/pasted national digits at what the selected country can
// actually hold, so the field can't accumulate an endless run of digits.
// Driven entirely by libphonenumber-js metadata — no hardcoded per-country
// lengths — and only ever trims from the end, so it can't reject a digit
// that a longer valid number would need. Mid-typing lengths that are merely
// *invalid* (TOO_SHORT, or INVALID_LENGTH between two valid lengths) are
// left alone; only TOO_LONG is trimmed.
export function capNationalDigits(nationalDigits, country) {
  const callingCode = getCountryCallingCode(country);
  let capped = (nationalDigits || "").slice(0, E164_MAX_DIGITS - callingCode.length);

  while (
    capped.length > 1 &&
    validatePhoneNumberLength(`+${callingCode}${capped}`, country) === "TOO_LONG"
  ) {
    capped = capped.slice(0, -1);
  }

  return capped;
}

// Splits a raw digit string (calling code + national number) into the two
// parts this component tracks separately, given which country is selected.
export function splitDigits(digits, country) {
  if (!digits) return { callingCode: "", nationalDigits: "" };
  const callingCode = country ? getCountryCallingCode(country) : "";
  const nationalDigits = digits.startsWith(callingCode)
    ? digits.slice(callingCode.length)
    : digits;
  return { callingCode, nationalDigits };
}

// Whether `digits` (the wire format above: calling code + national number)
// is a real, dialable number. For forms that need to gate submission — the
// component's own error only appears on blur, and an untouched field is
// silently invalid.
export function isPhoneValid(digits) {
  const parsed = parsePhoneNumberFromString(toE164((digits || "").trim()));
  return Boolean(parsed?.isValid());
}

// One message per failure mode, in the same short/direct Ukrainian style as
// the rest of the funnel's validation copy (see DataForm's "Заповни всі
// поля..." error).
export function getPhoneError({ digits, nationalDigits, country, required }) {
  if (!(nationalDigits || "").trim()) {
    return required ? "Введи номер телефону" : "";
  }

  const e164 = toE164((digits || "").trim());
  const lengthResult = validatePhoneNumberLength(e164, country);

  if (lengthResult === "TOO_SHORT") return "Номер телефону неповний";
  if (lengthResult === "TOO_LONG") return "Забагато цифр у номері телефону";
  if (lengthResult === "INVALID_COUNTRY") return "Оберіть країну для номера телефону";

  const parsed = parsePhoneNumberFromString(e164, country);
  if (!parsed || !parsed.isValid()) {
    return "Некоректний номер телефону для обраної країни";
  }

  return "";
}
